/**
 * Minimal Markdown → HTML renderer. Optimized for LLM output: covers the
 * ~95% of formatting models actually produce — headings, bold, italic,
 * inline code, fenced code, links, auto-linked URLs, lists, blockquotes —
 * without shipping a full CommonMark parser.
 *
 * All user-facing text is HTML-escaped first, then markdown markers are
 * transformed on the escaped string. Fenced code blocks are extracted
 * before the inline pass so their contents stay literal.
 *
 * ~1.5 KB minified — good tradeoff versus ~30 KB for marked / remark.
 */

export function renderMarkdown(raw: string): string {
  if (!raw) return '';
  // 1. Extract fenced code blocks first so their contents aren't processed
  //    by the inline passes. Replace with placeholders, restore at the end.
  const codeBlocks: string[] = [];
  let text = raw.replace(/```(\w*)\n?([\s\S]*?)```/g, (_m, lang: string, body: string) => {
    codeBlocks.push(
      `<pre><code${lang ? ` class="lang-${escapeAttr(lang)}"` : ''}>${escapeHtml(body.replace(/\n$/, ''))}</code></pre>`,
    );
    return `\0CODEBLOCK${codeBlocks.length - 1}\0`;
  });

  // 2. Escape everything else up front. Subsequent regex replacements build
  //    HTML from these escaped characters.
  text = escapeHtml(text);

  // 3. Headings — # / ## / ### at line start.
  text = text.replace(/^###\s+(.+)$/gm, '<h3>$1</h3>');
  text = text.replace(/^##\s+(.+)$/gm, '<h2>$1</h2>');
  text = text.replace(/^#\s+(.+)$/gm, '<h1>$1</h1>');

  // 4. Blockquotes (one line each for now — good enough for LLM asides).
  text = text.replace(/^&gt;\s?(.+)$/gm, '<blockquote>$1</blockquote>');

  // 5. Unordered + ordered lists — grouped so consecutive items become one <ul>/<ol>.
  text = groupList(text, /^(?:[-*+])\s+(.+)$/gm, 'ul');
  text = groupList(text, /^(\d+)\.\s+(.+)$/gm, 'ol');

  // 6. Inline formatting — order matters: code before bold/italic so **foo**
  //    inside `backticks` stays literal.
  text = text.replace(/`([^`\n]+?)`/g, (_m, inner: string) => `<code>${inner}</code>`);
  text = text.replace(/\*\*([^*\n]+?)\*\*/g, '<strong>$1</strong>');
  text = text.replace(/(?<!\*)\*([^*\n]+?)\*(?!\*)/g, '<em>$1</em>');

  // 7. Explicit markdown links [text](url).
  text = text.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, (_m, label: string, url: string) => {
    return `<a href="${escapeAttr(url)}" target="_blank" rel="noopener noreferrer nofollow">${label}</a>`;
  });

  // 8. Autolink bare URLs — skip ones already wrapped in an <a>.
  text = text.replace(/(^|[\s(])(https?:\/\/[^\s)<]+)/g, (_m, pre: string, url: string) => {
    // Trailing punctuation like "url." shouldn't be part of the href.
    const trail = url.match(/[.,;:!?]+$/);
    const clean = trail ? url.slice(0, -trail[0].length) : url;
    const tail = trail ? trail[0] : '';
    return `${pre}<a href="${escapeAttr(clean)}" target="_blank" rel="noopener noreferrer nofollow">${clean}</a>${tail}`;
  });

  // 9. Paragraph + line-break splitting. Blank line → </p><p>. Single \n → <br>.
  const paragraphs = text.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
  text = paragraphs
    .map((p) => (isBlockLevel(p) ? p : `<p>${p.replace(/\n/g, '<br />')}</p>`))
    .join('');

  // 10. Restore fenced code blocks.
  text = text.replace(/\0CODEBLOCK(\d+)\0/g, (_m, i: string) => codeBlocks[Number(i)] ?? '');

  return text;
}

/**
 * Collapse consecutive lines matched by `marker` into a single <ul> / <ol>.
 * Other lines pass through. Works line-by-line so nested prose + lists
 * interleave correctly.
 */
function groupList(text: string, marker: RegExp, tag: 'ul' | 'ol'): string {
  const lines = text.split('\n');
  const out: string[] = [];
  let buffer: string[] = [];
  const flush = () => {
    if (buffer.length === 0) return;
    out.push(`<${tag}>${buffer.map((b) => `<li>${b}</li>`).join('')}</${tag}>`);
    buffer = [];
  };
  for (const line of lines) {
    // Each iteration needs a fresh RegExp because /g regexes carry state.
    const m = new RegExp(marker.source).exec(line);
    if (m) {
      // Ordered lists capture the number in group 1 and content in group 2,
      // unordered captures content in group 1 — take the last non-undefined group.
      buffer.push(m[m.length - 1] ?? '');
    } else {
      flush();
      out.push(line);
    }
  }
  flush();
  return out.join('\n');
}

/**
 * Detect "already a block element" so we don't wrap it in another <p>.
 * Cheap heuristic: starts with one of the known block tags we emit above.
 */
function isBlockLevel(s: string): boolean {
  return /^<(h[1-6]|ul|ol|pre|blockquote)\b/.test(s);
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}

function escapeAttr(s: string): string {
  return escapeHtml(s);
}
